import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const { loginToken, loginTokenCoUser } = getState();
  const token = loginToken?.response?.token || loginTokenCoUser?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function buildTagsQuery({ type = "all", search = "" } = {}) {
  const params = new URLSearchParams();
  if (type) {
    params.set("type", type);
  }
  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    params.set("search", trimmedSearch);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const fetchTags = createAsyncThunk(
  "tags/fetch",
  async ({ businessId, type = "all", search = "" }, { getState, rejectWithValue }) => {
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/tags/${businessId}${buildTagsQuery({ type, search })}`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const createTag = createAsyncThunk(
  "tags/create",
  async ({ type, name, business_id }, { getState, rejectWithValue }) => {
    if (!business_id) {
      return rejectWithValue({ message: "Missing business id" });
    }
    if (!type) {
      return rejectWithValue({ message: "Missing tag type" });
    }
    if (!name?.trim()) {
      return rejectWithValue({ message: "Missing tag name" });
    }
    try {
      const response = await axios.post(
        `${ROOT_URL}/tags`,
        { type, name: name.trim(), business_id },
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const deleteTag = createAsyncThunk(
  "tags/delete",
  async ({ tagId }, { getState, rejectWithValue }) => {
    if (!tagId) {
      return rejectWithValue({ message: "Missing tag id" });
    }
    try {
      const response = await axios.delete(
        `${ROOT_URL}/tags/${tagId}`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

const initialState = {
  list: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

const tagsApi = createSlice({
  name: "tags",
  initialState,
  reducers: {
    clearTags: () => initialState,
    clearTagsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.loading = false;
        state.list = Array.isArray(action.payload?.data) ? action.payload.data : [];
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.list = [];
      })
      .addCase(createTag.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createTag.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createTag.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteTag.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteTag.fulfilled, (state, action) => {
        state.deleting = false;
        const deletedId = action.meta.arg?.tagId;
        if (deletedId) {
          state.list = state.list.filter((tag) => tag._id !== deletedId);
        }
      })
      .addCase(deleteTag.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearTags, clearTagsError } = tagsApi.actions;
export default tagsApi.reducer;
